/**
 * useCapacityUtilisation Hook
 * US-013D Phase 2: State management for capacity utilisation
 */

import { useState, useCallback, useEffect } from 'react'
import {
  getUtilisation,
  detectConflicts,
  resolveConflict,
  logDowntime,
  assessDowntimeImpact,
  getResources,
  getDowntimes
} from './capacityService'
import { toLocalDateString } from '@/utils/dateUtils'

/**
 * Hook for managing capacity utilisation state
 *
 * @param {Object} options
 * @param {boolean} [options.autoLoad=true] - Automatically load data on mount
 * @param {number} [options.daysAhead=14] - Days to look ahead
 * @returns {Object} Hook state and functions
 */
export function useCapacityUtilisation(options = {}) {
  const { autoLoad = true, daysAhead = 14 } = options

  // Utilisation state
  const [utilisation, setUtilisation] = useState(null)
  const [byDate, setByDate] = useState([])
  const [byResource, setByResource] = useState([])

  // Conflicts state
  const [conflicts, setConflicts] = useState([])
  const [conflictSummary, setConflictSummary] = useState(null)

  // Resources state
  const [resources, setResources] = useState([])
  const [downtimes, setDowntimes] = useState([])

  // Loading states
  const [loading, setLoading] = useState(false)
  const [conflictsLoading, setConflictsLoading] = useState(false)
  const [downtimesLoading, setDowntimesLoading] = useState(false)

  // Error states
  const [error, setError] = useState(null)
  const [conflictsError, setConflictsError] = useState(null)

  // Selected state
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedResource, setSelectedResource] = useState(null)

  // ==========================================================================
  // Load Utilisation Data
  // ==========================================================================

  const loadUtilisation = useCallback(async (loadOptions = {}) => {
    setLoading(true)
    setError(null)

    try {
      const today = toLocalDateString(new Date())
      const endDate = toLocalDateString(new Date(Date.now() + (daysAhead - 1) * 24 * 60 * 60 * 1000))

      const result = await getUtilisation({
        start_date: loadOptions.start_date || today,
        end_date: loadOptions.end_date || endDate,
        resource_id: loadOptions.resource_id
      })

      setUtilisation(result)
      setByDate(result.by_date || [])
      setByResource(result.by_resource || [])

      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Load error:', err)
      setError(err.message || 'Failed to load utilisation data')
      throw err
    } finally {
      setLoading(false)
    }
  }, [daysAhead])

  // ==========================================================================
  // Load Conflicts
  // ==========================================================================

  const loadConflicts = useCallback(async (loadOptions = {}) => {
    setConflictsLoading(true)
    setConflictsError(null)

    try {
      const today = toLocalDateString(new Date())
      const endDate = toLocalDateString(new Date(Date.now() + (daysAhead - 1) * 24 * 60 * 60 * 1000))

      const result = await detectConflicts({
        start_date: loadOptions.start_date || today,
        end_date: loadOptions.end_date || endDate
      })

      setConflicts(result.conflicts || [])
      setConflictSummary(result.summary || null)

      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Conflicts error:', err)
      setConflictsError(err.message || 'Failed to load conflicts')
      throw err
    } finally {
      setConflictsLoading(false)
    }
  }, [daysAhead])

  // ==========================================================================
  // Load Resources
  // ==========================================================================

  const loadResources = useCallback(async () => {
    try {
      const result = await getResources()
      setResources(result)
      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Resources error:', err)
      throw err
    }
  }, [])

  // ==========================================================================
  // Load Downtimes
  // ==========================================================================

  const loadDowntimes = useCallback(async (loadOptions = {}) => {
    setDowntimesLoading(true)

    try {
      const result = await getDowntimes({
        resource_id: loadOptions.resource_id
      })
      setDowntimes(result)
      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Downtimes error:', err)
      throw err
    } finally {
      setDowntimesLoading(false)
    }
  }, [])

  // ==========================================================================
  // Resolve Conflict
  // ==========================================================================

  const getResolution = useCallback(async (date, resourceId) => {
    try {
      const result = await resolveConflict(date, resourceId)
      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Resolution error:', err)
      throw err
    }
  }, [])

  // ==========================================================================
  // Log Downtime
  // ==========================================================================

  const createDowntime = useCallback(async (downtimeData) => {
    try {
      const result = await logDowntime(downtimeData)

      // Reload downtimes and utilisation
      await Promise.all([
        loadDowntimes(),
        loadUtilisation()
      ])

      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Create downtime error:', err)
      throw err
    }
  }, [loadDowntimes, loadUtilisation])

  // ==========================================================================
  // Assess Downtime Impact
  // ==========================================================================

  const assessImpact = useCallback(async (downtimeId) => {
    try {
      const result = await assessDowntimeImpact(downtimeId)
      return result
    } catch (err) {
      console.error('[useCapacityUtilisation] Assess impact error:', err)
      throw err
    }
  }, [])

  // ==========================================================================
  // Refresh All
  // ==========================================================================

  const refresh = useCallback(async () => {
    await Promise.all([
      loadUtilisation(),
      loadConflicts(),
      loadResources(),
      loadDowntimes()
    ])
  }, [loadUtilisation, loadConflicts, loadResources, loadDowntimes])

  // ==========================================================================
  // Auto-load on mount
  // ==========================================================================

  useEffect(() => {
    if (autoLoad) {
      loadUtilisation()
      loadConflicts()
      loadResources()
      loadDowntimes()
    }
  }, [autoLoad, loadUtilisation, loadConflicts, loadResources, loadDowntimes])

  // ==========================================================================
  // Derived Data
  // ==========================================================================

  // Chart data for 14-day view
  const chartData = byDate.map(day => ({
    date: day.date,
    dateLabel: formatDateLabel(day.date),
    ...day.resources.reduce((acc, r) => {
      acc[r.resource_code] = r.utilisation_pct
      acc[`${r.resource_code}_used`] = r.total_used
      acc[`${r.resource_code}_capacity`] = r.capacity_value
      acc[`${r.resource_code}_status`] = r.status
      return acc
    }, {}),
    hasConflict: day.has_conflict,
    totalUtilisation: day.total_utilisation_pct
  }))

  // Days with conflicts
  const daysWithConflicts = byDate.filter(d => d.has_conflict).map(d => d.date)

  // Has any conflicts
  const hasConflicts = conflicts.length > 0

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // Utilisation data
    utilisation,
    byDate,
    byResource,
    chartData,

    // Conflicts data
    conflicts,
    conflictSummary,
    daysWithConflicts,
    hasConflicts,

    // Resources & downtimes
    resources,
    downtimes,

    // Loading states
    loading,
    conflictsLoading,
    downtimesLoading,

    // Error states
    error,
    conflictsError,

    // Selection
    selectedDate,
    selectedResource,
    setSelectedDate,
    setSelectedResource,

    // Actions
    loadUtilisation,
    loadConflicts,
    loadResources,
    loadDowntimes,
    getResolution,
    createDowntime,
    assessImpact,
    refresh
  }
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatDateLabel(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })
}

export default useCapacityUtilisation
